/**
 * Bepaalt of we bij een maatoptie zelf voorgestelde formaten tonen die niet uit
 * de catalogus komen, maar uit de vrije maat worden afgeleid (20 x 20 cm tot
 * 50 x 50 cm, binnen de grenzen van de eigen maat).
 *
 * Achtergrond: de controle stond eerst inline in `withVirtualCustomSizePresets`
 * in `product-configurator.tsx` en keek alleen of het om de optie `size` ging.
 *
 * Beleid (Gert, 16 okt 2026):
 *
 * 1. In het algemeen blijven de extra formaten staan, ook bij Print.com en ook
 *    naast vaste maten. Een eerder, strenger beleid (niet bij Print.com, niet
 *    als er al vaste maten zijn) is afgewezen: die extra formaatkeuzes zijn met
 *    opzet in de winkel gebouwd en verdwenen toen van alle Print.com-producten
 *    tegelijk. De bijbehorende testverwachtingen staan overgeslagen in het
 *    testbestand hiernaast.
 * 2. Uitzondering: verpakkingen. Een vlakke 30 x 30 cm naast "Verzenddoos
 *    225 x 150 x 90 mm" of "Magneetdoos ..." is voor de klant onzin. Daarom geen
 *    extra formaten als de optie al vaste 3D-maten heeft (drie maten in het
 *    label), of als het product een verpakking is (categorie of slug zoals
 *    verpakkingen, dozen, boxes, sleeves, packaging).
 */

type OptionValueLike = {
  value?: unknown;
  label?: unknown;
  customSizes?: unknown;
  metadata?: { printcomName?: unknown } | null;
};

type OptionLike = {
  id?: string;
  values?: OptionValueLike[];
};

export type VirtualPresetProductContext = {
  productSlug?: string | null;
  productCategory?: string | null;
};

function isCustomSizeValue(value: OptionValueLike): boolean {
  return Boolean(value?.customSizes) || String(value?.value ?? '') === 'custom';
}

/** "Verzenddoos 225 x 150 x 90 mm", "30x20x10 cm": een maat met drie afmetingen. */
export function isThreeDimensionalSizeValue(value: OptionValueLike | null | undefined): boolean {
  const text = `${value?.label || ''} ${value?.value || ''} ${value?.metadata?.printcomName || ''}`
    .toLowerCase()
    .replace(/×/g, 'x');
  return /\d+(?:[.,]\d+)?\s*x\s*\d+(?:[.,]\d+)?\s*x\s*\d+(?:[.,]\d+)?\s*(?:cm|mm)?/.test(text);
}

/** Losse woorden in een slug of categorie die op een verpakking wijzen. */
const PACKAGING_TOKENS = new Set([
  'verpakking', 'verpakkingen', 'verzendverpakking', 'verzendverpakkingen',
  'packaging', 'package', 'packages',
  'doos', 'dozen', 'verzenddoos', 'verzenddozen', 'magneetdoos', 'magneetdozen', 'brievenbusdoos', 'brievenbusdozen',
  'box', 'boxes', 'mailer', 'mailers',
  'sleeve', 'sleeves',
]);

/**
 * "laptop-sleeves" en "tablet-sleeve" zijn hoesjes, geen verpakking, en een
 * "puzzel-met-doos" is een puzzel; een "lightbox" of "fotobox" is één woord en
 * telt dus niet als `box`.
 */
const NON_PACKAGING_CONTEXT_TOKENS = new Set(['laptop', 'tablet', 'ipad', 'phone', 'telefoon', 'macbook', 'puzzel', 'puzzels', 'puzzle', 'puzzles']);

function tokens(text: unknown): string[] {
  return String(text || '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

export function isPackagingProduct(context: VirtualPresetProductContext | null | undefined): boolean {
  if (!context) return false;
  return [context.productSlug, context.productCategory].some((text) => {
    const parts = tokens(text);
    if (parts.some((part) => NON_PACKAGING_CONTEXT_TOKENS.has(part))) return false;
    return parts.some((part) => PACKAGING_TOKENS.has(part));
  });
}

export function shouldOfferVirtualCustomSizePresets(
  option: OptionLike | null | undefined,
  providerType?: string,
  context?: VirtualPresetProductContext | null,
): boolean {
  void providerType;
  if (option?.id !== 'size') return false;
  const fixedValues = (option.values || []).filter((value) => !isCustomSizeValue(value));
  if (fixedValues.some(isThreeDimensionalSizeValue)) return false;
  if (isPackagingProduct(context)) return false;
  return true;
}
